<script setup lang="ts">
    import { ChevronRight } from 'lucide-vue-next';
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogTrigger,
        DialogContent,
        DialogHeader,
        DialogTitle,
        DialogDescription,
        DialogClose,
        DialogFooter,
        DialogBody,
    } from '@/components/ui/dialog';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { useMutation } from '@tanstack/vue-query';
    import { usePage } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import axios from 'axios';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData } from '@/types';
    import { DatePicker, Checkbox } from '@/components/ui/form';
    import { Label } from '@/components/ui/label';
    import { computed, ref } from 'vue';
    import { useDateFormat } from '@/composables/useDateFormat';
    import { today, getLocalTimeZone } from '@internationalized/date';

    const queryClient = useQueryClient();

    const page = usePage<SharedData>();
    const companyId = computed(() => page.props.auth.user.selected_company_id);

    const { formatDMY } = useDateFormat();

    const { t } = useI18n();

    const sendToCustomer = ref(false);
    const deadline = ref(null);
    const isOpen = ref(false);

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: {
            company_id: number;
            send_to_customer: boolean;
            deadline: string | null;
        }) => axios.post(route('v2.voucher.admin.release.all'), formData),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.VOUCHERS, companyId.value] });

            isOpen.value = false;
            toast.success(data.data.success);
        },
        onError: (error: any) => {
            toast.error(error.response.data.message || t('forms.system_error'));
        },
    });

function uploadAll() {
        mutate({
            company_id: companyId.value,
            send_to_customer: sendToCustomer.value,
            deadline: deadline.value,
        });
    }

    function handleCancel() {
        isOpen.value = false;
        sendToCustomer.value = false;
        deadline.value = null;
    }
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogTrigger as-child>
            <Button
                variant="ghost_primary"
                width="narrow"
                class="self-start"
                size="xs"
            >
                {{ $t('vouchers.release') }}
                <ChevronRight class="text-primary" />
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-lg">
            <DialogHeader>
                <DialogTitle>{{ t('vouchers.release_all.title') }}</DialogTitle>
            </DialogHeader>

            <DialogBody class="space-y-5">
                <DialogDescription>
                    {{ t('vouchers.release_all.content') }}
                </DialogDescription>

                <Checkbox
                    id="sendToCustomer"
                    v-model="sendToCustomer"
                    label="vouchers.release_all.send_to_customer"
                />

                <div
                    v-if="sendToCustomer"
                    class="flex flex-col gap-2"
                >
                    <Label>{{ $t('vouchers.release_all.release_label') }}</Label>
                    <DatePicker
                        v-model="deadline"
                        :label="$t('vouchers.release_all.release_label')"
                        :min-date="today(getLocalTimeZone())"
                    />
                </div>
            </DialogBody>

            <DialogFooter class="gap-2 border-t sm:justify-center">
                <DialogClose as-child>
                    <Button
                        variant="outline"
                        size="sm"
                        @click="handleCancel"
                    >
                        {{ $t('forms.cancel') }}
                    </Button>
                </DialogClose>

                <Button
                    :disabled="isPending || (sendToCustomer && !deadline)"
                    :loading="isPending"
                    size="sm"
                    @click="uploadAll"
                >
                    {{ $t('vouchers.release_all.button') }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
